import {ProtocolQuery} from '../service-model/protocol-query.interface';
import {SHELL_PROTOCOL_OPERATIONS_NS} from '../shell-communication/operations/shell-protocol-namespace.const';

// ── GetSkewSentiment: one-shot on mount for initial value ──

export class GetSkewSentiment implements ProtocolQuery {
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetSkewSentiment`;
    }
}

export interface GetSkewSentimentReply {
    value: number | null;
    tier: string;
    method: string;
    chosenExpiration: string;
    errorMessage: string;
    timestamp: string | null;
}

// ── Expiration breakdown row (used in both push and debug-query payloads) ──

export interface ExpirationResultDto {
    dte: number;
    expirationDate: string;
    method: string;
    value: number | null;
    bracketWidth: number | null;
    reason: string;
}

// ──────────────────────────────────────────────────────────────────────
// SkewSentimentChangedDto: pushed via EventStream on every value change.
// Now carries the full debug payload so the debug panel is always in
// sync with the displayed value — no more "chasing ghosts" from a
// separately-fetched debug snapshot.
// ──────────────────────────────────────────────────────────────────────

export interface SkewSentimentChangedDto {
    value: number;
    hasValue: boolean;
    tier: string;
    method: string;
    timestamp: string;

    chosenExpiration: string;
    errorMessage: string;
    atm: number;
    spxPrice: number;
    spreadWidth: number;
    targetPrice: number;
    majorityValue: number | null;
    majorityCount: number;
    totalProducing: number;
    averageOfProducing: number | null;
    excludedOutliers: string[];
    expirations: ExpirationResultDto[];

    // Stability-gate diagnostics. If pendingConfirmCount > 0, the debug
    // panel should render a "Candidate: {v} ({n}/{K})" row so the user
    // can see why a newly-computed reading hasn't yet replaced the
    // displayed value.
    pendingValue: number | null;
    pendingIsNA: boolean;
    pendingConfirmCount: number;
    confirmThreshold: number;

    // Anchor-and-Confirm diagnostics. When resolvedByAnchor is true, the
    // debug panel renders the anchor expiration plus its supporters list
    // so the user sees why the value was picked over a cluster majority.
    resolvedByAnchor: boolean;
    anchorExpiration: string;
    anchorSupporters: string[];
    anchorDeniers: string[];
}

// ── GetSkewSentimentDebug: backfill on first debug-panel open if the ──
// ── service hasn't received a push yet. Kept as a safety net only.   ──

export class GetSkewSentimentDebug implements ProtocolQuery {
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetSkewSentimentDebug`;
    }
}

export interface GetSkewSentimentDebugReply {
    value: number | null;
    tier: string;
    method: string;
    chosenExpiration: string;
    errorMessage: string;
    atm: number;
    spxPrice: number;
    spreadWidth: number;
    targetPrice: number;
    majorityValue: number | null;
    majorityCount: number;
    totalProducing: number;
    averageOfProducing: number | null;
    excludedOutliers: string[];
    expirations: ExpirationResultDto[];

    pendingValue: number | null;
    pendingIsNA: boolean;
    pendingConfirmCount: number;
    confirmThreshold: number;

    resolvedByAnchor: boolean;
    anchorExpiration: string;
    anchorSupporters: string[];
    anchorDeniers: string[];
}
